import React from 'react';
import { LogIn, UserPlus, Zap } from 'lucide-react';
import { useAuth, useGuestMode } from '../../lib/auth';
import { useBilling } from '../../lib/billing';
import { AUTH_CONFIG, BILLING_CONFIG } from '../../config';
import { t } from '../../utils/i18n';

const GuestPrompt = ({ onProceedAsGuest }) => {
  const { signIn } = useAuth();
  const { canUseAsGuest, remainingQuota } = useGuestMode();
  const { plans } = useBilling();

  const handleSignIn = async () => {
    try {
      await signIn();
    } catch (error) {
      console.error('Sign in failed:', error);
    }
  };

  // If guest mode is not allowed or quota exceeded
  if (!AUTH_CONFIG.UI.SHOW_GUEST_MODE || !canUseAsGuest) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <LogIn className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t('signInRequired')}
            </h2>
            <p className="text-gray-600 mb-6">
              {!canUseAsGuest
                ? t('guestLimitBody')
                : t('signInRequiredBody')
              }
            </p>

            <div className="space-y-3">
              <button
                onClick={handleSignIn}
                className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                {t('signInToContinue')}
              </button>
            </div>

            {BILLING_CONFIG.ENABLED && (
              <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <Zap className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-purple-800">{t('upgradeBenefits')}</span>
                </div>
                <div className="text-xs text-gray-600 space-y-1">
                  {plans.map(plan => (
                    <div key={plan.id}>
                      • {plan.features[0]}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Guest mode allowed
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserPlus className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('welcome')}
          </h2>
          <p className="text-gray-600 mb-6">
            {t('guestChoiceBody')}
          </p>

          {/* Guest quota info */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
            <div className="text-sm text-yellow-800">
              <strong>{t('guestModeLabel')}</strong> {t('guestRequestsRemaining', { count: remainingQuota })}
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleSignIn}
              className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              {t('signInFullAccess')}
            </button>
            <button
              onClick={onProceedAsGuest}
              className="w-full bg-gray-100 text-gray-700 px-4 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              {t('continueAsGuest', { count: remainingQuota })}
            </button>
          </div>

          {BILLING_CONFIG.ENABLED && (
            <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <Zap className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-800">{t('premiumFeatures')}</span>
              </div>
              <div className="text-xs text-gray-600 space-y-1">
                <div>✓ {t('unlimitedRequests')}</div>
                <div>✓ {t('prioritySupport')}</div>
                <div>✓ {t('advancedFeatures')}</div>
                <div>✓ {t('usageAnalytics')}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuestPrompt;
